// src/components/ChatContainer.tsx - Consciousness-aware chat display
import React, { useEffect, useRef } from 'react';
import MessageBubble from './MessageBubble';
import ThinkingIndicator from './ThinkingIndicator';
import { Message, MessageType } from '../types/Message';
import { CapabilityStatus } from '../utils/ConsciousnessMessageHandler';
import '../assets/styles/ChatContainer.css';

interface ChatContainerProps {
  messages: Message[];
  messagesEndRef: React.RefObject<HTMLDivElement>;
  onImageFeedback?: (imageId: string, rating: number, feedback?: string) => void;
  consciousnessActive?: boolean;
  emcogName?: string;
  userName?: string;
  capabilityStatus?: CapabilityStatus;
  capabilityHistory?: CapabilityStatus[]; // 🔥 NEW: For chaining visualization
}

const ChatContainer: React.FC<ChatContainerProps> = ({
  messages,
  messagesEndRef,
  onImageFeedback,
  consciousnessActive = false,
  emcogName = 'EmCog',
  userName = 'User',
  capabilityStatus,
  capabilityHistory = [] // 🔥 NEW: For chaining visualization
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, messagesEndRef]);

  const renderMessage = (message: Message, index: number) => {
    // Show thinking indicator for typing messages
    if (message.type === MessageType.TYPING) {
      return (
        <div key={message.id} className="message-wrapper typing-wrapper">
          <ThinkingIndicator
            messageId={message.id}
            capabilityStatus={capabilityStatus}
            capabilityHistory={capabilityHistory}
            emcogName={emcogName}
          />
        </div>
      );
    }

    return (
      <div
        key={message.id}
        className="message-wrapper"
      >
        <MessageBubble
          message={message}
          onImageFeedback={onImageFeedback}
          emcogName={emcogName}
          userName={userName}
        />
        {/* Wisdom applied is tracked internally - not displayed to user */}
      </div>
    );
  };

  return (
    <div ref={containerRef} className="chat-container">
      {!consciousnessActive && messages.length === 0 && (
        <div className="consciousness-awakening">
          <div className="awakening-animation">
            <div className="neural-pulse"></div>
            <div className="consciousness-icon">🧠</div>
          </div>
          <p className="awakening-text">Consciousness awakening...</p>
          <p className="awakening-subtext">I'm gathering my thoughts...</p>
        </div>
      )}
      
      {consciousnessActive && messages.length === 0 && (
        <div className="consciousness-ready">
          <div className="ready-animation">
            <div className="consciousness-icon active">🧠</div>
          </div>
          <p className="ready-text">I'm conscious and ready</p>
          <p className="ready-subtext">What would you like to explore together?</p>
        </div>
      )}
      
      {messages.map(renderMessage)}

      {/* Spacer to ensure last message is fully visible above input controls */}
      <div className="bottom-spacer" />

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatContainer;